import React from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "./Button";
import { isAndroid } from "react-device-detect";

interface CustomDatePickerProps {
  value: string | null; // Expected format: "YYYY-MM-DD"
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  className?: string;
  customInput?: React.ReactNode;
  id?: string;
  name?: string;
  autoComplete?: string;
  wrapperClassName?: string;
  showMonthsDropdown?: boolean;
  popperPlacement?: string;
  openToDate?: Date;
}

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  readOnly?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const LivlyDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = "Select a date",
  className = "border border-gray-300 px-3 py-2 rounded-md",
  customInput,
  id,
  name,
  autoComplete,
  wrapperClassName,
  showMonthsDropdown = false,
  popperPlacement = "top-end",
  openToDate
}) => {
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(
    value ? new Date(value) : null
  );

  // Custom input component to prevent keyboard on mobile
  const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
    (props, ref) => (
      <input
        className="cursor-pointer"
        {...props}
        ref={ref}
        readOnly={true}
        onFocus={(e) => {
          if (isAndroid) {
            e.preventDefault(); // Prevent keyboard from opening
          }
          props.onFocus?.(e);
        }}
      />
    )
  );

  return (
    <DatePicker
      portalId="root-portal"
      popperPlacement={popperPlacement}
      selected={selectedDate}
      withPortal={isAndroid}
      onChange={(date: Date | null) => {
        setSelectedDate(date);
        onChange(date);
      }}
      className={className}
      placeholderText={placeholder}
      showYearDropdown
      scrollableYearDropdown
      minDate={minDate}
      maxDate={maxDate}
      openToDate={openToDate}
      customInput={customInput || <CustomInput />}
      id={id}
      name={name}
      autoComplete={autoComplete}
      wrapperClassName={wrapperClassName}
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth
      }: {
        date: Date;
        changeYear: (year: number) => void;
        changeMonth: (month: number) => void;
        decreaseMonth: () => void;
        increaseMonth: () => void;
      }) => {
        const currentYear = date.getFullYear();
        const currentMonth = date.getMonth();

        const minYear = minDate?.getFullYear();
        const maxYear = maxDate?.getFullYear();
        const minMonth = minDate?.getMonth();
        const maxMonth = maxDate?.getMonth();

        const isPrevDisabled =
          minDate &&
          (currentYear < minYear! ||
            (currentYear === minYear && currentMonth <= minMonth!));

        const isNextDisabled =
          maxDate &&
          (currentYear > maxYear! ||
            (currentYear === maxYear && currentMonth >= maxMonth!));

        const yearOptions = (() => {
          if (minYear !== undefined && maxYear !== undefined) {
            return Array.from(
              { length: maxYear - minYear + 1 },
              (_, i) => minYear + i
            );
          } else {
            // Default range if no min/max year is passed
            const baseYear = currentYear;
            return Array.from({ length: 30 }, (_, i) => baseYear - 15 + i);
          }
        })();

        const monthOptions = Array.from({ length: 12 }, (_, i) => i);
        return (
          <div className="flex items-center justify-between px-2 py-1">
            {/* < button */}
            <Button
              onClick={decreaseMonth}
              disabled={isPrevDisabled}
              size="small"
              outlined
            >
              <FontAwesomeIcon icon="chevron-left" />
            </Button>

            {/* Month + Year */}
            <div className="flex items-center gap-2">
              {showMonthsDropdown ? (
                <select
                  value={currentMonth}
                  onChange={(e) => changeMonth(Number(e.target.value))}
                  className="border rounded text-sm"
                >
                  {monthOptions.map((monthIdx) => (
                    <option key={monthIdx} value={monthIdx}>
                      {moment().month(monthIdx).format("MMMM")}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-sm font-medium">
                  {moment(date).format("MMMM")}
                </span>
              )}

              <select
                value={currentYear}
                onChange={(e) => changeYear(Number(e.target.value))}
                className="border rounded text-sm overflow-scroll"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* > button */}
            <Button
              onClick={increaseMonth}
              disabled={isNextDisabled}
              size="small"
              outlined
            >
              <FontAwesomeIcon icon="chevron-right" />
            </Button>
          </div>
        );
      }}
    />
  );
};
